import { Breadcrumb, Select, TextField, TitleScreen, Tooltip } from "components/Core"
import useOrchResources from "hooks/customs/useOrchResources"
import useSearchMiddlewareOSRM from "hooks/useSearchMiddlewareOSRM"
import React, { useEffect, useState, useMemo, useCallback, memo } from "react"
import { Link, useNavigate, useParams } from "react-router-dom"
import cn from "classnames"
import usePreRender from "hooks/usePreRender"
import { format_date, format_time } from "helpers/datetime_helper"
import {
  HiPencil,
  HiOutlineDuplicate,
  HiChevronRight,
  HiChevronDown,
  HiRefresh,
  HiOutlineTruck,
  HiOutlineOfficeBuilding,
  HiOutlineQuestionMarkCircle,
  HiSearch,
  HiX,
} from "react-icons/hi"
import InfiniteScroll from "react-infinite-scroll-component"
import CloneItemModal from "./CloneItemModal"
import InventoryAdjustmentModal from "./InventoryAdjustmentModal"
import SgModalView from "./SgModalView"
import getTranslateConfig from "translates/config"
import { handleRecategorizeAction } from "helpers/recategorize_helper"
import UseShippingGroupActions from "hooks/customs/UseShippingGroupActions"
import { MdInventory } from "react-icons/md"

// iOS-style Collapsible Data Group (Standardized)
const DataGroupComponent = ({
  title,
  children,
  defaultOpen = true,
  className = "",
  themeColor = "blue-600",
}: {
  title: string
  children: React.ReactNode
  defaultOpen?: boolean
  className?: string
  themeColor?: string
}) => {
  const [isOpen, setIsOpen] = useState(defaultOpen)

  return (
    <div className={cn("w-full flex flex-col mb-4", className)}>
      <div
        className={cn(
          "w-full bg-white rounded-[12px] border border-gray-200 shadow-sm transition-all duration-300",
          isOpen ? "overflow-visible" : "overflow-hidden",
        )}
      >
        {/* Header Area */}
        <div
          onClick={() => setIsOpen(!isOpen)}
          className={cn(
            "w-full px-6 py-4 flex flex-row items-center justify-between cursor-pointer transition-colors select-none",
            "bg-gray-50/80 hover:bg-gray-100/50",
            isOpen && "border-b border-gray-100",
          )}
        >
          <div className="flex flex-row items-center gap-3">
            <div className={cn("w-1.5 h-5 rounded-full", `bg_${themeColor}`)}></div>
            <h3 className="text-[14px] font-bold text-gray-700 uppercase tracking-wide">{title}</h3>
          </div>
          <div className="text-gray-400 transition-transform duration-300">
            {isOpen ? <HiChevronDown className="text-xl" /> : <HiChevronRight className="text-xl" />}
          </div>
        </div>

        {/* Content Area */}
        <div
          className={cn(
            "transition-all duration-500 ease-in-out",
            !isOpen ? "max-h-0 opacity-0 pointer-events-none" : "max-h-[5000px] opacity-100",
          )}
        >
          <div className="flex flex-col">{children}</div>
        </div>
      </div>
    </div>
  )
}
const DataGroup = memo(DataGroupComponent)

const DataItemComponent = ({
  label,
  value,
  last,
  isTitle,
  isCentered,
}: {
  label?: string
  value: any
  last?: boolean
  isTitle?: boolean
  isCentered?: boolean
}) => {
  const isEmpty = value === null || value === undefined || value === ""

  if (isCentered) {
    return (
      <div
        className={`flex flex-row justify-center items-center py-4 px-4 ${!last ? "border-b border-gray-100" : ""} bg-gray-50/50`}
      >
        <p className="text-[16px] font-black uppercase tracking-widest text-gray-800">
          {isEmpty ? "[Sin Categoría]" : String(value)}
        </p>
      </div>
    )
  }

  return (
    <div className={`flex flex-row justify-between items-center py-3 px-4 ${!last ? "border-b border-gray-100" : ""}`}>
      <p className={cn("text-[15px] text-gray-800", isTitle ? "font-black uppercase tracking-tight" : "font-normal")}>
        {label}
      </p>
      <p
        className={cn(
          "text-[15px] text-right max-w-[60%] break-all",
          isTitle ? "font-black" : "font-medium",
          isEmpty ? "text-gray-300 italic" : "text-gray-500",
        )}
      >
        {isEmpty ? "[Vacío]" : String(value)}
      </p>
    </div>
  )
}
const DataItem = memo(DataItemComponent)

const DetailScreen = () => {
  const [item, set_data] = useState<any>()
  const [isCloneModalOpen, setIsCloneModalOpen] = useState(false)
  const [isInventoryModalOpen, setIsInventoryModalOpen] = useState(false)
  const [isCloning, setIsCloning] = useState(false)
  const { _color } = usePreRender()
  const navigate = useNavigate()
  const { item_id } = useParams()
  const [current_channel, set_current_channel] = useState<string>("")
  const [current_source, set_current_source] = useState<string>("")
  const [source_items, set_source_items] = useState<any[]>([])
  const [source_error, set_source_error] = useState<string | null>(null)
  const {
    data: data_items,
    search_osrm_action: search_osrm_action_items,
    set_loading_app,
  } = useSearchMiddlewareOSRM({
    entity: "items",
  })
  const { post_source_items_inventory_by_channel_action } = useOrchResources()
  const [channels, set_channels] = useState([])
  const { data: data_channels, search_osrm_action: search_osrm_action_channels } = useSearchMiddlewareOSRM({
    entity: "channels",
  })
  const {
    data: data_sgs,
    search_osrm_action: search_osrm_action_sgs,
    action_search_osrm_action: action_search_osrm_action_sgs,
  } = useSearchMiddlewareOSRM({
    entity: "shipping_groups",
  })
  const { data: data_incident_sgs, search_osrm_action: search_osrm_action_incident_sgs } = useSearchMiddlewareOSRM({
    entity: "shipping_groups",
  })

  // Hook for actions
  const { putUpdateInfoOrderAction } = UseShippingGroupActions()

  const [orders, setOrders] = useState<any[]>([])
  const [sgsFrom, setSgsFrom] = useState(0)
  const [hasMoreSgs, setHasMoreSgs] = useState(true)
  const [searchTerm, setSearchTerm] = useState("")
  const [activeSearchTerm, setActiveSearchTerm] = useState("")
  const [statusFilter, setStatusFilter] = useState("ALL")

  const [incidentOrders, setIncidentOrders] = useState<any[]>([])
  const [incidentFrom, setIncidentFrom] = useState(0)
  const [hasMoreIncidentSgs, setHasMoreIncidentSgs] = useState(true)
  const [incidentSearchTerm, setIncidentSearchTerm] = useState("")
  const [activeIncidentSearchTerm, setActiveIncidentSearchTerm] = useState("")
  const [incidentFilter, setIncidentFilter] = useState("MISCATEGORIZED")

  const [selectedSg, setSelectedSg] = useState<any>(null)
  const [selectedSource, setSelectedSource] = useState<any>(null)

  const translations: any = useMemo(() => getTranslateConfig() || {}, [])

  const getTranslatedStatus = useCallback(
    (sg: any) => {
      const sId = sg?._omnixStateMachine?.currentState?.state?.id
      const sName = sg?._omnixStateMachine?.currentState?.state?.name

      // Attempt to translate using the state ID (e.g., "CONFIRMED", "DELIVERED")
      if (sId) {
        const upperId = sId.toUpperCase()
        if (translations[upperId] && translations[upperId].title) {
          return translations[upperId].title
        }
      }

      // Fallback to name or default
      return sName || "Estado Desconocido"
    },
    [translations],
  )

  const getOrderStatusInfo = useCallback((sg: any) => {
    const sName = sg?._omnixStateMachine?.currentState?.state?.name || ""
    const sId = sg?._omnixStateMachine?.currentState?.state?.id || ""

    const upperId = sId.toUpperCase()
    const lowerName = sName.toLowerCase()

    // Status resolution based on specific criteria
    if (
      upperId === "CLOSED" ||
      upperId === "DELIVERED" ||
      upperId === "PICKED_UP" ||
      upperId === "DELIVERED_IN_STORE" ||
      upperId === "DELIVERED_IN_SOURCE" ||
      lowerName.includes("entregad") ||
      lowerName.includes("cerrad") ||
      lowerName.includes("retirad")
    ) {
      return { color: "bg-gray-400", label: "Cerrada/Entregada", code: 2 }
    }
    if (upperId === "CANCELED" || lowerName.includes("cancel")) {
      return { color: "bg-red-500", label: "Cancelada", code: 3 }
    }

    // Defaulting to "Active"
    return { color: "bg-green-500", label: "Activa", code: 1 }
  }, [])

  const getDeliveryIcon = useCallback((sg: any) => {
    // Usually target.type or storePickup
    const isStorePickup = sg?.shippingType === "SP"
    const isHomeDelivery = sg?.shippingType === "HD"

    if (isStorePickup) {
      return (
        <Tooltip content="Retiro en tienda" align="top">
          <div className="flex items-center justify-center bg-gray-100 rounded p-1 text-gray-500">
            <HiOutlineOfficeBuilding className="text-lg" />
          </div>
        </Tooltip>
      )
    }

    if (isHomeDelivery) {
      return (
        <Tooltip content="Despacho a domicilio" align="top">
          <div className="flex items-center justify-center bg-gray-100 rounded p-1 text-gray-500">
            <HiOutlineTruck className="text-lg" />
          </div>
        </Tooltip>
      )
    }

    return (
      <Tooltip content="Tipo de entrega desconocido" align="top">
        <div className="flex items-center justify-center bg-gray-100 rounded p-1 text-gray-500">
          <HiOutlineQuestionMarkCircle className="text-lg" />
        </div>
      </Tooltip>
    )
  }, [])

  const getItemQuantity = useCallback(
    (sg: any) => {
      const items = sg?.logisticPlan?.items || []
      const itemInSg = items.find((i: any) => i.sku === item_id)
      return itemInSg?.quantity || 0
    },
    [item_id],
  )

  const fetchSgs = useCallback(
    (fromIndex: number, search?: string, sFilt?: string, sourceIdOverride?: string | null) => {
      const currentFilter = sFilt || statusFilter
      const currentSource = sourceIdOverride !== undefined ? sourceIdOverride : selectedSource?.source

      const filters: any = { "logisticPlan.items.sku": item_id }
      let excludes: any = undefined

      if (search) {
        filters.salesChannelId = search
      }

      if (currentSource) {
        filters["source.id"] = currentSource
      }

      if (currentFilter === "ACTIVE") {
        excludes = { "_omnixStateMachine.currentState.state.name": ["CANCELED", "DELIVERED", "CLOSED"] }
      } else if (currentFilter === "CLOSED") {
        filters["_omnixStateMachine.currentState.state.name"] = ["DELIVERED", "CLOSED", "PICKED_UP"]
      } else if (currentFilter === "CANCELED") {
        filters["_omnixStateMachine.currentState.state.name"] = "CANCELED"
      }

      const queryParams: any = {
        from: fromIndex,
        size: 20,
        filters,
        sortBy: { orderCreationDate: "desc" },
      }

      if (excludes) {
        queryParams.excludes = excludes
      }

      search_osrm_action_sgs(queryParams)
    },
    [item_id, statusFilter, selectedSource, search_osrm_action_sgs],
  )

  const fetchIncidentSgs = useCallback(
    (fromIndex: number, search?: string, filter?: string, sourceIdOverride?: string | null) => {
      const currentSource = sourceIdOverride !== undefined ? sourceIdOverride : selectedSource?.source
      const currentFilter = filter || incidentFilter

      const filters: any = {
        "logisticPlan.items.sku": item_id,
      }

      let excludes: any = undefined

      if (currentFilter === "MISCATEGORIZED") {
        filters["_omnixStateMachine.currentState.state.name"] = [
          "ASSIGNED",
          "IN_PREPARATION",
          "PICKING_ID_GENERATED",
          "PICKING_IN_PROGRESS",
          "VALIDATE_PICKING",
        ]

        if (item?.categories && Object.values(item.categories).length > 0) {
          excludes = {
            "logisticPlan.items.type": Object.values(item.categories)[0],
          }
        }
      }

      if (search) {
        filters.salesChannelId = search
      }

      if (currentSource) {
        filters["source.id"] = currentSource
      }

      const queryParams: any = {
        from: fromIndex,
        size: 20,
        filters,
        sortBy: { orderCreationDate: "desc" },
      }

      if (excludes) {
        queryParams.excludes = excludes
      }

      search_osrm_action_incident_sgs(queryParams)
    },
    [item, item_id, selectedSource, incidentFilter, search_osrm_action_incident_sgs],
  )

  const handleRecategorize = useCallback(
    (e: React.MouseEvent, sg: any) => {
      e.stopPropagation()

      const newType =
        item?.categories && Object.values(item.categories).length > 0 ? Object.values(item.categories)[0] : undefined

      if (!newType) {
        return
      }

      handleRecategorizeAction({
        sg,
        itemSku: item_id as string,
        newType: newType as string,
        action_search_osrm_action: action_search_osrm_action_sgs,
        putUpdateInfoOrderAction,
        onSuccess: () => {
          setIncidentFrom(0)
          setIncidentOrders([])
          setHasMoreIncidentSgs(true)
          fetchIncidentSgs(0, activeIncidentSearchTerm, incidentFilter)
        },
      })
    },
    [
      item,
      item_id,
      action_search_osrm_action_sgs,
      putUpdateInfoOrderAction,
      activeIncidentSearchTerm,
      incidentFilter,
      fetchIncidentSgs,
    ],
  )

  const handleSourceSelect = useCallback(
    (sourceItem: any) => {
      setSelectedSource(sourceItem)
      setSgsFrom(0)
      setOrders([])
      setHasMoreSgs(true)
      fetchSgs(0, activeSearchTerm, statusFilter, sourceItem.source)

      setIncidentFrom(0)
      setIncidentOrders([])
      setHasMoreIncidentSgs(true)
      fetchIncidentSgs(0, activeIncidentSearchTerm, incidentFilter, sourceItem.source)
    },
    [activeSearchTerm, statusFilter, fetchSgs, activeIncidentSearchTerm, incidentFilter, fetchIncidentSgs],
  )

  const clearSourceFilter = useCallback(() => {
    setSelectedSource(null)
    setSgsFrom(0)
    setOrders([])
    setHasMoreSgs(true)
    fetchSgs(0, activeSearchTerm, statusFilter, null)

    setIncidentFrom(0)
    setIncidentOrders([])
    setHasMoreIncidentSgs(true)
    fetchIncidentSgs(0, activeIncidentSearchTerm, incidentFilter, null)
  }, [activeSearchTerm, statusFilter, fetchSgs, activeIncidentSearchTerm, incidentFilter, fetchIncidentSgs])

  const handleSearch = useCallback(() => {
    setActiveSearchTerm(searchTerm)
    setSgsFrom(0)
    setOrders([])
    setHasMoreSgs(true)
    fetchSgs(0, searchTerm, statusFilter)
  }, [searchTerm, statusFilter, fetchSgs])

  const clearSearch = useCallback(() => {
    setSearchTerm("")
    setActiveSearchTerm("")
    setSgsFrom(0)
    setOrders([])
    setHasMoreSgs(true)
    fetchSgs(0, "", statusFilter)
  }, [statusFilter, fetchSgs])

  const handleIncidentSearch = useCallback(() => {
    setActiveIncidentSearchTerm(incidentSearchTerm)
    setIncidentFrom(0)
    setIncidentOrders([])
    setHasMoreIncidentSgs(true)
    fetchIncidentSgs(0, incidentSearchTerm, incidentFilter)
  }, [incidentSearchTerm, incidentFilter, fetchIncidentSgs])

  const clearIncidentSearch = useCallback(() => {
    setIncidentSearchTerm("")
    setActiveIncidentSearchTerm("")
    setIncidentFrom(0)
    setIncidentOrders([])
    setHasMoreIncidentSgs(true)
    fetchIncidentSgs(0, "", incidentFilter)
  }, [incidentFilter, fetchIncidentSgs])

  const handleIncidentFilterChange = useCallback(
    (val: string) => {
      setIncidentFilter(val)
      setIncidentFrom(0)
      setIncidentOrders([])
      setHasMoreIncidentSgs(true)
      fetchIncidentSgs(0, activeIncidentSearchTerm, val)
    },
    [activeIncidentSearchTerm, fetchIncidentSgs],
  )

  const handleStatusFilterChange = useCallback(
    (val: string) => {
      setStatusFilter(val)
      setSgsFrom(0)
      setOrders([])
      setHasMoreSgs(true)
      fetchSgs(0, activeSearchTerm, val)
    },
    [activeSearchTerm, fetchSgs],
  )

  useEffect(() => {
    fetchSgs(0)
    search_osrm_action_channels({ from: 0, size: 1000, fields: ["id", "name"] })
    search_osrm_action_items({ from: 0, size: 1, filters: { sku: item_id } })
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  useEffect(() => {
    if (item) {
      fetchIncidentSgs(0)
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [item])

  useEffect(() => {
    if (data_sgs?.records) {
      if (sgsFrom === 0) {
        setOrders(data_sgs.records)
      } else {
        setOrders((prev) => {
          const combined = [...prev, ...data_sgs.records]
          const unique = combined.filter((v, i, a) => a.findIndex((t) => t.id === v.id) === i)
          return unique
        })
      }

      if (data_sgs.records.length < 20) {
        setHasMoreSgs(false)
      } else {
        setHasMoreSgs(true)
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [data_sgs])

  const loadMoreSgs = useCallback(() => {
    if (!hasMoreSgs) return
    setSgsFrom((prev) => {
      const newFrom = prev + 20
      fetchSgs(newFrom, activeSearchTerm, statusFilter)
      return newFrom
    })
  }, [hasMoreSgs, activeSearchTerm, statusFilter, fetchSgs])

  useEffect(() => {
    if (data_incident_sgs?.records) {
      if (incidentFrom === 0) {
        setIncidentOrders(data_incident_sgs.records)
      } else {
        setIncidentOrders((prev) => {
          const combined = [...prev, ...data_incident_sgs.records]
          const unique = combined.filter((v, i, a) => a.findIndex((t) => t.id === v.id) === i)
          return unique
        })
      }

      if (data_incident_sgs.records.length < 20) {
        setHasMoreIncidentSgs(false)
      } else {
        setHasMoreIncidentSgs(true)
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [data_incident_sgs])

  const loadMoreIncidentSgs = useCallback(() => {
    if (!hasMoreIncidentSgs) return
    setIncidentFrom((prev) => {
      const newFrom = prev + 20
      fetchIncidentSgs(newFrom, activeIncidentSearchTerm, incidentFilter)
      return newFrom
    })
  }, [hasMoreIncidentSgs, activeIncidentSearchTerm, incidentFilter, fetchIncidentSgs])

  const handleClone = useCallback(() => {
    setIsCloning(true)
    setTimeout(() => {
      setIsCloning(false)
      setIsCloneModalOpen(true)
    }, 1000)
  }, [])

  const CloningOverlay = () =>
    isCloning ? (
      <div className="fixed inset-0 z-[100] flex flex-col items-center justify-center bg-white/80 backdrop-blur-sm transition-all duration-300">
        <div className="flex flex-col items-center gap-4 p-8 rounded-3xl bg-white shadow-2xl border border-gray-100">
          <div className="relative h-16 w-16 flex items-center justify-center">
            <HiRefresh className={cn("h-12 w-12 animate-spin", `text_${_color}`)} />
          </div>
          <div className="flex flex-col items-center">
            <h3 className="text-xl font-black text-gray-800 tracking-tight">Clonando artículo</h3>
            <p className="text-sm font-medium text-gray-400">Preparando datos del nuevo artículo...</p>
          </div>
        </div>
      </div>
    ) : null

  const get_source_items = useCallback(async () => {
    if (!current_channel) return
    set_loading_app(true)
    set_source_error(null)
    const body = { filter: [item_id] }
    try {
      const response: any = await post_source_items_inventory_by_channel_action({ body, channel: current_channel })
      set_source_items(response?.data?.message || [])
    } catch (error) {
      set_source_error("No hay informacion disponible.")
    } finally {
      set_loading_app(false)
    }
  }, [current_channel, item_id, post_source_items_inventory_by_channel_action, set_loading_app])

  useEffect(() => {
    get_source_items()
  }, [get_source_items])

  console.log("source_items", source_items)

  useEffect(() => {
    if (Boolean(data_items?.records.length)) {
      const get_item = async () => {
        let item_from_req = data_items?.records[0]
        set_data(item_from_req)
      }
      get_item()
    }
    let channels_from_req = data_channels?.records
    set_channels(
      channels_from_req?.map((chl: any) => ({
        id: chl?.id,
        label: `${chl?.name} (${chl?.id})`,
        value: chl?.id,
      })),
    )
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [data_items, data_channels])

  const filterSources = useCallback(
    (query: string) => {
      try {
        const regex = new RegExp(query, "i")
        return source_items.filter((src: any) => regex.test(src.name) || regex.test(src.source))
      } catch (error) {
        return []
      }
    },
    [source_items],
  )

  const sources_to_show: any = useMemo(() => {
    return current_source ? filterSources(current_source) : source_items
  }, [current_source, source_items, filterSources])

  return (
    <div className="w-full h-full overflow-auto flex flex-col items-center justify-start bg-[#F2F2F7]">
      <Breadcrumb
        data={[
          { name: "Listado de artículos", href: "/items" },
          { name: "Detalle de artículo", href: `/items/${item_id}` },
        ]}
      />
      <div className="w-full flex flex-col justify-center items-center py-4 px-6">
        <TitleScreen title={`Detalle de artículo #${item_id}`} />
      </div>

      <div className="w-full lg:w-5/6 flex flex-col">
        <div className="w-full flex flex-row items-center justify-end px-6 gap-3 mb-2">
          <Tooltip content="Ajustar Inventario">
            <button
              onClick={() => setIsInventoryModalOpen(true)}
              className={cn(
                "p-2 text-white hover:opacity-80 rounded-full shadow-sm hover:shadow-md transform transition duration-500 hover:scale-110 flex items-center justify-center",
                `bg_${_color}`,
              )}
            >
              <MdInventory className="text-xl" />
            </button>
          </Tooltip>
          <Tooltip content="Clonar artículo">
            <button
              onClick={handleClone}
              className={cn(
                "p-2 text-white hover:opacity-80 rounded-full shadow-sm hover:shadow-md transform transition duration-500 hover:scale-110 flex items-center justify-center",
                `bg_${_color}`,
              )}
            >
              <HiOutlineDuplicate className="text-xl" />
            </button>
          </Tooltip>
          <Tooltip content="Editar artículo">
            <Link
              to={`/items/${item_id}/edit`}
              className={cn(
                "p-2 text-white hover:opacity-80 rounded-full shadow-sm hover:shadow-md transform transition duration-500 hover:scale-110 flex items-center justify-center",
                `bg_${_color}`,
              )}
            >
              <HiPencil className="text-xl" />
            </Link>
          </Tooltip>
        </div>
        <div className="w-full flex flex-col lg:flex-row gap-x-8 py-4 px-6">
          <div className="w-full lg:w-1/2 flex flex-col">
            <DataGroup title="Información General" themeColor={_color}>
              <DataItem label="SKU" value={item_id} />
              <DataItem label="Nombre" value={item?.name} />
              <DataItem label="Estado" value={item?.enabled ? "Activo" : "Inactivo"} />
              <DataItem label="Tipo" value={item?.type} />
              <DataItem label="Retiro en tienda" value={item?.storePickup ? "Si" : "No"} last />
            </DataGroup>

            <DataGroup title="Atributos y Categorías" themeColor={_color}>
              {item?.categories && (
                <DataItem
                  value={Object.values(item.categories)[0]}
                  isCentered
                  last={!item?.custom || Object.keys(item.custom).length === 0}
                />
              )}
              {item?.custom &&
                Object.entries(item.custom).map(([key, value], idx, arr) => (
                  <DataItem key={key} label={key} value={value} last={idx === arr.length - 1} />
                ))}
              {!item?.categories && !item?.custom && (
                <p className="text-sm text-gray-400 italic px-4 py-3">No hay información disponible</p>
              )}
            </DataGroup>

            <DataGroup title="Tiempos de preparación" themeColor={_color}>
              {item?.preparationTime && Object.keys(item.preparationTime).length > 0 ? (
                Object.entries(item.preparationTime).map(([key, value], idx, arr) => (
                  <DataItem key={key} label={key} value={value} last={idx === arr.length - 1} />
                ))
              ) : (
                <p className="text-sm text-gray-400 italic px-4 py-3">No hay información disponible</p>
              )}
            </DataGroup>

            <DataGroup title="Atributos logísticos" themeColor={_color}>
              {item?.logisticAttributes && Object.keys(item.logisticAttributes).length > 0 ? (
                Object.entries(item.logisticAttributes).map(([key, value], idx, arr) => (
                  <DataItem key={key} label={key} value={value} last={idx === arr.length - 1} />
                ))
              ) : (
                <p className="text-sm text-gray-400 italic px-4 py-3">No hay información disponible</p>
              )}
            </DataGroup>
          </div>

          <div className="w-full lg:w-1/2 flex flex-col">
            <DataGroup title="Inventario por source" themeColor={_color}>
              <div className="p-4 flex flex-col gap-4">
                <Select
                  label="Canal"
                  options={channels}
                  value={current_channel}
                  onChange={(e) => set_current_channel(e)}
                  placeholder="Seleccione un canal..."
                />
                <div className="flex flex-col bg-[#F2F2F7] rounded-xl border border-gray-200 overflow-hidden">
                  {source_items.length ? (
                    <div className="w-full p-2 bg-white border-b border-gray-200">
                      <TextField
                        label=""
                        placeholder="Buscar por source..."
                        value={current_source}
                        onChange={(e: any) => set_current_source(e.target.value)}
                      />
                    </div>
                  ) : null}
                  {source_error ? (
                    <p className="text-sm text-red-500 px-4 py-3 bg-white">{source_error}</p>
                  ) : source_items.length ? (
                    <ul className="max-h-80 overflow-y-auto">
                      {sources_to_show?.map((item: any, index: number) => (
                        <li
                          key={index}
                          className="text-[14px] text-[#4C4C4C] px-4 py-3 border-b last:border-0 hover:bg-white transition-colors bg-white/50 flex justify-between items-center"
                        >
                          <label className="flex items-center gap-3 cursor-pointer">
                            <input
                              type="radio"
                              name="sourceSelection"
                              checked={selectedSource?.source === item.source}
                              onChange={() => handleSourceSelect(item)}
                              className="text-[#007AFF] focus:ring-[#007AFF] w-4 h-4 cursor-pointer"
                            />
                            <span className="font-semibold">
                              {item.name} ({item.source})
                            </span>
                          </label>
                          <span className="text-[#007AFF] font-bold">{item?.items[0]?.quantity}</span>
                        </li>
                      ))}
                    </ul>
                  ) : (
                    <p className="text-sm text-gray-400 italic px-4 py-8 text-center bg-white">
                      {current_channel ? "No hay stock disponible" : "Seleccione un canal para ver stock"}
                    </p>
                  )}
                </div>
              </div>
            </DataGroup>

            {typeof item?.safety !== "undefined" && (
              <DataGroup title="Stock de seguridad" themeColor={_color}>
                <DataItem
                  label="Configuración"
                  value={typeof item?.safety === "object" ? JSON.stringify(item.safety) : item?.safety}
                  last
                />
              </DataGroup>
            )}

            <DataGroup title="Órdenes relacionadas" themeColor={_color}>
              <div className="w-full flex flex-col gap-4 p-4">
                {/* Buscador */}
                <div className="w-full flex flex-col gap-2">
                  <div className="flex flex-row items-center gap-2">
                    <div className="flex-1 relative">
                      <input
                        type="text"
                        className="w-full h-10 pl-4 pr-10 text-[14px] bg-white border border-gray-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-[#007AFF]/20 focus:border-[#007AFF] transition-all"
                        placeholder="Buscar una órden..."
                        value={searchTerm}
                        onChange={(e) => setSearchTerm(e.target.value)}
                        onKeyDown={(e) => e.key === "Enter" && handleSearch()}
                      />
                      {searchTerm && (
                        <button
                          onClick={() => {
                            setSearchTerm("")
                            if (activeSearchTerm) clearSearch()
                          }}
                          className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-600 p-1"
                        >
                          <HiX className="text-sm" />
                        </button>
                      )}
                    </div>
                    <button
                      onClick={handleSearch}
                      className="h-10 px-4 flex items-center justify-center bg-[#007AFF] hover:bg-[#007AFF]/90 text-white rounded-lg transition-colors shadow-sm"
                    >
                      <HiSearch className="text-lg" />
                    </button>
                  </div>
                  {activeSearchTerm && (
                    <div className="flex items-center justify-between bg-blue-50/50 border border-blue-100 rounded-lg px-3 py-2">
                      <p className="text-[13px] text-blue-700 font-medium">
                        Mostrando resultados para: <span className="font-bold">"{activeSearchTerm}"</span>
                      </p>
                      <button
                        onClick={clearSearch}
                        className="text-[12px] font-bold text-blue-600 hover:text-blue-800 underline"
                      >
                        Limpiar filtro
                      </button>
                    </div>
                  )}

                  <div className="flex flex-row flex-wrap items-center gap-6 mt-1 mb-2 px-3 py-2 bg-gray-50 rounded-lg border border-gray-100">
                    <label className="flex items-center gap-2 cursor-pointer text-[13px] font-medium text-gray-700">
                      <input
                        type="radio"
                        name="statusFilter"
                        value="ALL"
                        checked={statusFilter === "ALL"}
                        onChange={() => handleStatusFilterChange("ALL")}
                        className="text-[#007AFF] focus:ring-[#007AFF] w-4 h-4"
                      />
                      Todas
                    </label>
                    <label className="flex items-center gap-2 cursor-pointer text-[13px] font-medium text-gray-700">
                      <input
                        type="radio"
                        name="statusFilter"
                        value="ACTIVE"
                        checked={statusFilter === "ACTIVE"}
                        onChange={() => handleStatusFilterChange("ACTIVE")}
                        className="text-[#007AFF] focus:ring-[#007AFF] w-4 h-4"
                      />
                      Activas
                    </label>
                    <label className="flex items-center gap-2 cursor-pointer text-[13px] font-medium text-gray-700">
                      <input
                        type="radio"
                        name="statusFilter"
                        value="CLOSED"
                        checked={statusFilter === "CLOSED"}
                        onChange={() => handleStatusFilterChange("CLOSED")}
                        className="text-[#007AFF] focus:ring-[#007AFF] w-4 h-4"
                      />
                      Cerradas
                    </label>
                    <label className="flex items-center gap-2 cursor-pointer text-[13px] font-medium text-gray-700">
                      <input
                        type="radio"
                        name="statusFilter"
                        value="CANCELED"
                        checked={statusFilter === "CANCELED"}
                        onChange={() => handleStatusFilterChange("CANCELED")}
                        className="text-[#007AFF] focus:ring-[#007AFF] w-4 h-4"
                      />
                      Canceladas
                    </label>
                    <div className="ml-auto flex items-center gap-3">
                      {selectedSource && (
                        <div className="flex items-center gap-1.5 px-3 py-1 bg-blue-50 border border-blue-100 rounded-full shadow-sm">
                          <span className="text-[12px] font-bold text-blue-700 uppercase tracking-tight">Source:</span>
                          <span
                            className="text-[13px] font-black text-blue-700 max-w-[120px] truncate"
                            title={selectedSource.name}
                          >
                            {selectedSource.name}
                          </span>
                          <button
                            onClick={clearSourceFilter}
                            className="ml-1 text-[#007AFF] hover:text-blue-800 transition-colors focus:outline-none"
                            title="Limpiar filtro de source"
                          >
                            <HiX className="text-[14px]" />
                          </button>
                        </div>
                      )}
                      <div className="flex items-center gap-1.5 px-3 py-1 bg-white border border-gray-100 rounded-full shadow-sm">
                        <span className="text-[12px] font-bold text-gray-400 uppercase tracking-tight">Total:</span>
                        <span className="text-[13px] font-black text-[#007AFF]">{data_sgs?.total || 0}</span>
                      </div>
                    </div>
                  </div>
                </div>

                <div className="flex flex-col gap-2">
                  <div
                    id="scrollableOrders"
                    className="h-[400px] overflow-y-auto bg-white border border-gray-100 rounded-xl relative shadow-sm"
                  >
                    {orders.length > 0 ? (
                      <InfiniteScroll
                        dataLength={orders.length}
                        next={loadMoreSgs}
                        hasMore={hasMoreSgs}
                        loader={
                          <div className="p-4 text-center text-sm font-medium text-gray-400">
                            Cargando más órdenes...
                          </div>
                        }
                        scrollableTarget="scrollableOrders"
                        endMessage={
                          <div className="p-4 text-center text-[13px] font-medium text-gray-400 italic">
                            No hay más órdenes
                          </div>
                        }
                      >
                        <div className="flex flex-col">
                          {orders.map((sg, idx) => {
                            const statusInfo = getOrderStatusInfo(sg)
                            return (
                              <div
                                onClick={() => setSelectedSg(sg)}
                                key={idx}
                                className="p-4 hover:bg-gray-50 transition-colors flex justify-between items-center group border-b border-gray-50 last:border-0 relative cursor-pointer"
                              >
                                <div className={cn("absolute left-0 top-0 bottom-0 w-1 rounded-l")} />

                                <div className="flex flex-row items-center gap-4 pl-2">
                                  {/* State Indicator Pillar */}
                                  <Tooltip content={`Orden ${statusInfo.label}`} align="top">
                                    <div className={cn("w-1.5 h-10 rounded-full shrink-0", statusInfo.color)} />
                                  </Tooltip>

                                  {/* Quantity Badge */}
                                  <Tooltip content="Cantidad pedida de este SKU" align="top">
                                    <div className="flex items-center justify-center min-w-[28px] h-[28px] px-1.5 rounded-full bg-[#007AFF]/10 border border-[#007AFF]/20 shadow-sm shrink-0">
                                      <span className="text-[13px] font-black text-[#007AFF]">
                                        {getItemQuantity(sg)}
                                      </span>
                                    </div>
                                  </Tooltip>

                                  <div className="flex flex-col">
                                    <div className="flex flex-row items-center gap-2">
                                      <span className="font-bold text-gray-700 group-hover:text-[#007AFF] transition-colors">
                                        {sg.salesChannelId || sg.id}
                                      </span>
                                      <Tooltip content="Copiar ID" align="top">
                                        <button
                                          onClick={(e) => {
                                            e.stopPropagation()
                                            navigator.clipboard.writeText(sg.salesChannelId || sg.id)
                                          }}
                                          className="text-gray-400 hover:text-[#007AFF] transition-colors focus:outline-none flex items-center"
                                        >
                                          <HiOutlineDuplicate className="text-[16px]" />
                                        </button>
                                      </Tooltip>
                                      {getDeliveryIcon(sg)}
                                    </div>
                                    <span className="text-[13px] font-medium text-gray-400 mt-1 capitalize">
                                      {getTranslatedStatus(sg)} • {format_date(sg?.orderCreationDate)}{" "}
                                      {format_time(sg?.orderCreationDate)}
                                    </span>
                                  </div>
                                </div>

                                <div className="flex items-center justify-center w-8 h-8 rounded-full bg-gray-50 group-hover:bg-[#007AFF]/10 transition-colors shrink-0">
                                  <HiChevronRight className="text-gray-400 group-hover:text-[#007AFF]" />
                                </div>
                              </div>
                            )
                          })}
                        </div>
                      </InfiniteScroll>
                    ) : (
                      <div className="w-full h-full min-h-[150px] flex items-center justify-center">
                        <p className="text-[14px] text-gray-400 italic">No se encontraron órdenes para este ítem</p>
                      </div>
                    )}
                  </div>
                </div>
              </div>
            </DataGroup>

            <DataGroup title="Órdenes con incidencias" themeColor={_color}>
              <div className="w-full flex flex-col gap-4 p-4">
                {/* Buscador de incidencias */}
                <div className="w-full flex flex-col gap-2">
                  <div className="flex flex-row items-center gap-2">
                    <div className="flex-1 relative">
                      <input
                        type="text"
                        className="w-full h-10 pl-4 pr-10 text-[14px] bg-white border border-gray-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-[#007AFF]/20 focus:border-[#007AFF] transition-all"
                        placeholder="Buscar órden con incidencias..."
                        value={incidentSearchTerm}
                        onChange={(e) => setIncidentSearchTerm(e.target.value)}
                        onKeyDown={(e) => e.key === "Enter" && handleIncidentSearch()}
                      />
                      {incidentSearchTerm && (
                        <button
                          onClick={() => {
                            setIncidentSearchTerm("")
                            if (activeIncidentSearchTerm) clearIncidentSearch()
                          }}
                          className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-600 p-1"
                        >
                          <HiX className="text-sm" />
                        </button>
                      )}
                    </div>
                    <button
                      onClick={handleIncidentSearch}
                      className="h-10 px-4 flex items-center justify-center bg-[#007AFF] hover:bg-[#007AFF]/90 text-white rounded-lg transition-colors shadow-sm"
                    >
                      <HiSearch className="text-lg" />
                    </button>
                  </div>
                  {activeIncidentSearchTerm && (
                    <div className="flex items-center justify-between bg-blue-50/50 border border-blue-100 rounded-lg px-3 py-2">
                      <p className="text-[13px] text-blue-700 font-medium">
                        Mostrando resultados para: <span className="font-bold">"{activeIncidentSearchTerm}"</span>
                      </p>
                      <button
                        onClick={clearIncidentSearch}
                        className="text-[12px] font-bold text-blue-600 hover:text-blue-800 underline"
                      >
                        Limpiar filtro
                      </button>
                    </div>
                  )}

                  <div className="flex flex-row flex-wrap items-center gap-6 mt-1 mb-2 px-3 py-2 bg-gray-50 rounded-lg border border-gray-100 min-h-[44px]">
                    <label className="flex items-center gap-2 cursor-pointer text-[13px] font-medium text-gray-700">
                      <input
                        type="radio"
                        name="incidentFilter"
                        value="MISCATEGORIZED"
                        checked={incidentFilter === "MISCATEGORIZED"}
                        onChange={() => handleIncidentFilterChange("MISCATEGORIZED")}
                        className="text-[#007AFF] focus:ring-[#007AFF] w-4 h-4"
                      />
                      Mal categorizadas
                    </label>
                    <div className="ml-auto flex items-center gap-3 w-full justify-end">
                      {selectedSource && (
                        <div className="flex items-center gap-1.5 px-3 py-1 bg-blue-50 border border-blue-100 rounded-full shadow-sm">
                          <span className="text-[12px] font-bold text-blue-700 uppercase tracking-tight">Source:</span>
                          <span
                            className="text-[13px] font-black text-blue-700 max-w-[120px] truncate"
                            title={selectedSource.name}
                          >
                            {selectedSource.name}
                          </span>
                          <button
                            onClick={clearSourceFilter}
                            className="ml-1 text-[#007AFF] hover:text-blue-800 transition-colors focus:outline-none"
                            title="Limpiar filtro de source"
                          >
                            <HiX className="text-[14px]" />
                          </button>
                        </div>
                      )}
                      <div className="flex items-center gap-1.5 px-3 py-1 bg-white border border-gray-100 rounded-full shadow-sm">
                        <span className="text-[12px] font-bold text-gray-400 uppercase tracking-tight">Total:</span>
                        <span className="text-[13px] font-black text-[#007AFF]">{data_incident_sgs?.total || 0}</span>
                      </div>
                    </div>
                  </div>
                </div>

                <div className="flex flex-col gap-2">
                  <div
                    id="scrollableIncidentOrders"
                    className="h-[400px] overflow-y-auto bg-white border border-gray-100 rounded-xl relative shadow-sm"
                  >
                    {incidentOrders.length > 0 ? (
                      <InfiniteScroll
                        dataLength={incidentOrders.length}
                        next={loadMoreIncidentSgs}
                        hasMore={hasMoreIncidentSgs}
                        loader={
                          <div className="p-4 text-center text-sm font-medium text-gray-400">
                            Cargando más órdenes...
                          </div>
                        }
                        scrollableTarget="scrollableIncidentOrders"
                        endMessage={
                          <div className="p-4 text-center text-[13px] font-medium text-gray-400 italic">
                            No hay más órdenes
                          </div>
                        }
                      >
                        <div className="flex flex-col">
                          {incidentOrders.map((sg, idx) => {
                            const statusInfo = getOrderStatusInfo(sg)
                            return (
                              <div
                                onClick={() => setSelectedSg(sg)}
                                key={idx}
                                className="p-4 hover:bg-gray-50 transition-colors flex justify-between items-center group border-b border-gray-50 last:border-0 relative cursor-pointer"
                              >
                                <div className={cn("absolute left-0 top-0 bottom-0 w-1 rounded-l")} />

                                <div className="flex flex-row items-center gap-4 pl-2">
                                  {/* State Indicator Pillar */}
                                  <Tooltip content={`Orden ${statusInfo.label}`} align="top">
                                    <div className={cn("w-1.5 h-10 rounded-full shrink-0", statusInfo.color)} />
                                  </Tooltip>

                                  {/* Quantity Badge */}
                                  <Tooltip content="Cantidad pedida de este SKU" align="top">
                                    <div className="flex items-center justify-center min-w-[28px] h-[28px] px-1.5 rounded-full bg-[#007AFF]/10 border border-[#007AFF]/20 shadow-sm shrink-0">
                                      <span className="text-[13px] font-black text-[#007AFF]">
                                        {getItemQuantity(sg)}
                                      </span>
                                    </div>
                                  </Tooltip>

                                  <div className="flex flex-col">
                                    <div className="flex flex-row items-center gap-2">
                                      <span className="font-bold text-gray-700 group-hover:text-[#007AFF] transition-colors">
                                        {sg.salesChannelId || sg.id}
                                      </span>
                                      <Tooltip content="Copiar ID" align="top">
                                        <button
                                          onClick={(e) => {
                                            e.stopPropagation()
                                            navigator.clipboard.writeText(sg.salesChannelId || sg.id)
                                          }}
                                          className="text-gray-400 hover:text-[#007AFF] transition-colors focus:outline-none flex items-center"
                                        >
                                          <HiOutlineDuplicate className="text-[16px]" />
                                        </button>
                                      </Tooltip>
                                      {getDeliveryIcon(sg)}
                                    </div>
                                    <span className="text-[13px] font-medium text-gray-400 mt-1 capitalize">
                                      {getTranslatedStatus(sg)} • {format_date(sg?.orderCreationDate)}{" "}
                                      {format_time(sg?.orderCreationDate)}
                                    </span>
                                  </div>
                                </div>

                                <div className="flex flex-row items-center gap-2 shrink-0">
                                  <Tooltip content="Recategorizar" align="top">
                                    <button
                                      onClick={(e) => handleRecategorize(e, sg)}
                                      className="flex items-center justify-center w-8 h-8 rounded-full bg-blue-50 text-blue-500 hover:bg-blue-600 hover:text-white transition-colors focus:outline-none"
                                    >
                                      <HiRefresh className="text-[16px]" />
                                    </button>
                                  </Tooltip>
                                  <div className="flex items-center justify-center w-8 h-8 rounded-full bg-gray-50 group-hover:bg-[#007AFF]/10 transition-colors shrink-0">
                                    <HiChevronRight className="text-gray-400 group-hover:text-[#007AFF]" />
                                  </div>
                                </div>
                              </div>
                            )
                          })}
                        </div>
                      </InfiniteScroll>
                    ) : (
                      <div className="w-full h-full min-h-[150px] flex items-center justify-center">
                        <p className="text-[14px] text-gray-400 italic">No se encontraron órdenes para este ítem</p>
                      </div>
                    )}
                  </div>
                </div>
              </div>
            </DataGroup>
          </div>
        </div>
      </div>
      <CloneItemModal
        mounted={isCloneModalOpen}
        onClose={() => setIsCloneModalOpen(false)}
        itemToClone={item}
        onSuccess={(newSku) => navigate(`/items/${newSku}`)}
      />
      <CloningOverlay />

      <InventoryAdjustmentModal
        mounted={isInventoryModalOpen}
        onClose={() => setIsInventoryModalOpen(false)}
        sku={item_id}
        onSuccess={() => {
          get_source_items()
          setIsInventoryModalOpen(false)
        }}
      />

      {/* SG Detail Modal */}
      {selectedSg && <SgModalView sg={selectedSg} onClose={() => setSelectedSg(null)} />}
    </div>
  )
}

export default DetailScreen
